<script setup lang="ts">
import type { IRateResponse, IRateTooltipParam } from '~/components/widget/rates'
import type { ToolboxComponentOption } from 'echarts'
import { ref, computed, watch } from 'vue'
import { CHART_DATE_RANGES } from '~/lib/constants'
import { LazyVChart } from '~/components/ui/chart'

// Props interface for the UI component
interface Props {
  title: string
  data: IRateResponse | null
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
})

const { t } = useI18n()

interface Emits {
  (e: 'update:currency', currency: string): void
  (e: 'update:cashless', cashless: boolean): void
}

const emit = defineEmits<Emits>()

const currencies = ['usd', 'eur', 'rub', 'kzt'] as const
type Currency = (typeof currencies)[number]

const selectedCurrency = ref<Currency>('usd')
const cashless = ref<boolean>(false)
const selectedRange = ref('lastMonth')

const customFrom = ref<string>('')
const customTo = ref<string>('')

const ranges = CHART_DATE_RANGES

const selectRange = (key: string) => {
  selectedRange.value = key
  customFrom.value = ''
  customTo.value = ''
}

watch(selectedCurrency, (newCurrency) => {
  emit('update:currency', newCurrency)
})

watch(cashless, (newCashless) => {
  emit('update:cashless', newCashless)
})

watch([customFrom, customTo], ([from, to]) => {
  if (from && to && new Date(from) < new Date(to)) {
    selectedRange.value = 'custom'
  }
})

const xAxisData = computed(
  () => props.data?.buy?.map(([timestamp]) => new Date(timestamp).toISOString()) ?? []
)
const buySeriesData = computed(() => props.data?.buy?.map(([, value]) => value) ?? [])
const sellSeriesData = computed(() => props.data?.sell?.map(([, value]) => value) ?? [])

const getStartIndexForRange = (range: string, lastDate: Date, xAxisData: string[]) => {
  let startDate
  switch (range) {
    case 'lastMonth':
      startDate = new Date(lastDate)
      startDate.setMonth(lastDate.getMonth() - 1)
      break
    case 'last3Months':
      startDate = new Date(lastDate)
      startDate.setMonth(lastDate.getMonth() - 3)
      break
    case 'last6Months':
      startDate = new Date(lastDate)
      startDate.setMonth(lastDate.getMonth() - 6)
      break
    case 'lastYear':
      startDate = new Date(lastDate.getFullYear() - 1, lastDate.getMonth(), lastDate.getDate())
      break
    case 'fromBeginningOfYear':
      startDate = new Date(lastDate.getFullYear(), 0, 1)
      break
    case 'allTime':
      return 0
    default:
      return 0
  }
  const index = xAxisData.findIndex((date) => new Date(date) >= startDate)
  return index !== -1 ? index : 0
}

const customStartIndex = computed(() => {
  if (!customFrom.value || !customTo.value || !xAxisData.value.length) return 0
  const startDate = new Date(customFrom.value)
  const index = xAxisData.value.findIndex((date) => new Date(date) >= startDate)
  return index !== -1 ? index : 0
})

const customEndIndex = computed(() => {
  if (!customFrom.value || !customTo.value || !xAxisData.value.length)
    return xAxisData.value.length - 1
  const endDate = new Date(customTo.value)
  const index = xAxisData.value.findIndex((date) => new Date(date) > endDate)
  return index !== -1 ? index - 1 : xAxisData.value.length - 1
})

const chartOption = computed(() => {
  if (!xAxisData.value.length) return {}

  const lastDate = new Date(xAxisData.value[xAxisData.value.length - 1])

  const totalPoints = xAxisData.value.length

  const startIndex =
    selectedRange.value === 'custom'
      ? customStartIndex.value
      : getStartIndexForRange(selectedRange.value, lastDate, xAxisData.value)

  const endIndex =
    selectedRange.value === 'custom' ? customEndIndex.value : totalPoints - 1

  const startPercent = (startIndex / (totalPoints - 1)) * 100
  const endPercent = (endIndex / (totalPoints - 1)) * 100

  const visibleDays = endIndex > startIndex
    ? (new Date(xAxisData.value[endIndex]).getTime() - new Date(xAxisData.value[startIndex]).getTime()) / 86400000
    : 0

  const toolbox: ToolboxComponentOption = {
    feature: {
      saveAsImage: {
        show: true,
        title: `${t('common.save_title')} ${selectedCurrency.value.toUpperCase()}-${cashless.value ? t('common.cashless') : t('common.cash')}`,
        name: `${t('common.rate')}-${selectedCurrency.value.toUpperCase()}-${cashless.value ? t('common.cashless') : t('common.cash')}`,
        pixelRatio: 2,
        type: 'png',
        backgroundColor: '#fff'
      }
    },
    top: -5,
    right: -5,
    iconStyle: {
      borderColor: '#2563eb'
    }
  }

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        label: {
          show: true,
          backgroundColor: '#ffffff',
          color: '#000000',
          borderColor: '#000000',
          borderWidth: 1,
          borderRadius: 4,
          shadowColor: 'rgba(0, 0, 0, 0.3)',
          shadowBlur: 4,
          padding: [4, 4],
          fontSize: 12,
          formatter: (params: { value: string }) => {
            const date = new Date(params.value)
            return new Intl.DateTimeFormat('ru-RU', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }).format(date)
          }
        }
      },
      formatter: (params: IRateTooltipParam[]) => {
        const reversedParams = [...params].reverse()
        const date = new Date(reversedParams[0].axisValue)
        const dateStr = new Intl.DateTimeFormat('ru-RU', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(date)

        const lines = reversedParams
          .map(
            (p) => `
          <div style="margin: 4px 0;">
            <span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background:${p.color};"></span>
            <strong>${p.seriesName}: ${p.data.toFixed(2)}</strong>
          </div>
        `
          )
          .join('')

        return `<div><div style="margin-bottom:4px;">${dateStr}</div>${lines}</div>`
      }
    },
    legend: {
      data: [t('common.buy'), t('common.sell')],
      bottom: 0,
      orient: 'horizontal'
    },
    grid: {
      top: '10px',
      left: '30px',
      right: '30px',
      bottom: '70px',
      containLabel: true
    },
    xAxis: [
      {
        type: 'category',
        boundaryGap: false,
        axisLabel: {
          formatter: (value: string) => {
            const date = new Date(value)
            if (visibleDays <= 90) {
              return date.toLocaleString('ru-RU', { day: 'numeric', month: 'short' })
            }
            return date.toLocaleString('ru-RU', { month: 'short', year: '2-digit' })
          }
        },
        data: xAxisData.value
      },
      {
        type: 'category',
        position: 'bottom',
        offset: 40,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          formatter: (value: string) => {
            const date = new Date(value)
            return date.toLocaleString('ru-RU', { month: 'short', year: '2-digit' })
          }
        },
        axisPointer: { show: false },
        data: xAxisData.value
      }
    ],
    yAxis: {
      type: 'value',
      position: 'right',
      scale: true,
      splitNumber: 10,
      axisLabel: {
        formatter: (value: number) => value.toFixed(2)
      },
      name: t('common.price_in_kgs'),
      nameLocation: 'middle',
      nameRotate: -90,
      nameGap: 50,
      nameTextStyle: { fontSize: 12 },
      min: ({ min }: { min: number }) => min - 0.1,
      max: ({ max }: { max: number }) => max + 0.1
    },
    series: [
      {
        name: t('common.buy'),
        type: 'line',
        data: buySeriesData.value,
        color: '#2563eb',
        lineStyle: { width: 2 },
        xAxisIndex: 0
      },
      {
        name: t('common.sell'),
        type: 'line',
        data: sellSeriesData.value,
        color: '#22c55e',
        lineStyle: { width: 2 },
        xAxisIndex: 0
      }
    ],
    dataZoom: [
      {
        type: 'slider',
        xAxisIndex: [0],
        start: startPercent,
        end: endPercent,
        bottom: 40,
        brushSelect: false,
        minSpan: 1,
        showDetail: false
      }
    ],
    toolbox
  }
})
</script>

<template>
  <div>
    <div class="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-0">
      <Heading tag="h4">{{ $t('ui.bank_rates_chart.heading') }} {{ title }}</Heading>
      <div class="flex flex-col gap-4 md:flex-row">
        <div class="flex items-center space-x-2">
          <Switch
            id="mode"
            v-model="cashless"
            class="bg-blue-600 data-[state=checked]:bg-blue-600"
          />
          <Label for="mode">{{ $t('common.cashless') }}</Label>
        </div>
        <div class="flex gap-2">
          <Button
            v-for="currency in currencies"
            :key="currency"
            :variant="selectedCurrency === currency ? 'blue' : 'link'"
            size="sm"
            @click="selectedCurrency = currency"
          >
            {{ currency.toUpperCase() }}
          </Button>
        </div>
      </div>
    </div>
    <div class="flex flex-col justify-between gap-2 md:flex-row md:gap-0">
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-for="range in ranges"
          :key="range.key"
          size="sm"
          :variant="selectedRange === range.key ? 'blue' : 'link'"
          @click="selectRange(range.key)"
        >
          {{ range.label }}
        </Button>
      </div>
      <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div class="flex items-center gap-2">
          <span>{{ $t('common.from') }}</span>
          <Input
            v-model="customFrom"
            type="date"
            :max="customTo || undefined"
            class="max-w-[140px]"
          />
        </div>
        <div class="flex items-center gap-2">
          <span>{{ $t('common.to') }}</span>
          <Input
            v-model="customTo"
            type="date"
            :min="customFrom || undefined"
            class="max-w-[140px]"
          />
        </div>
      </div>
    </div>

    <div class="relative mt-4 h-[400px] w-full">
      <div v-if="loading" class="flex h-full items-center justify-center text-center text-gray-500">
        {{ $t('common.loading') }}
      </div>
      <div
        v-else-if="!data?.buy?.length || !data?.sell?.length"
        class="flex h-full items-center justify-center text-center text-gray-500"
      >
        {{ $t('common.no_data') }}
      </div>
      <LazyVChart v-else :option="chartOption" :update-options="{ notMerge: true }" autoresize class="h-full w-full" />
    </div>
  </div>
</template>
